import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CaretDown, ChatCircle, CaretRight, CircleNotch, Plus } from '@phosphor-icons/react';
import { PROJECT_BUCKETS, PROJECT_DOMAINS, can, type ProjectBucket, type ProjectDomain } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { BusinessHeader } from '../../../../../../components/businesses/business-header';
import { BusinessBreadcrumb, BusinessTabs } from '../../../../../../components/businesses/business-tabs';
import { Icon } from '../../../../../../components/Icon';
import { NewProjectDrawer } from '../../../../../../components/projects/new-project-drawer';
import { ProjectRow, ProjectRowsHeader, ProjectRowSkeleton } from '../../../../../../components/projects/project-row';
import { PageHeader } from '../../../../../../components/shell/page-header';
import { Card } from '../../../../../../components/ui/card';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { InfiniteList } from '../../../../../../components/ui/infinite-list';
import { SegmentedControl } from '../../../../../../components/ui/segmented-control';
import { useToast } from '../../../../../../components/ui/toast';
import { FilterSelectPill, pillClass, ToolbarSearch } from '../../../../../../components/ui/toolbar-controls';
import { businessQuery } from '../../../../../../lib/directory-api';
import { projectListQuery } from '../../../../../../lib/projects-api';
import { useSession } from '../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../lib/staff-nav';
import { useDebouncedValue } from '../../../../../../lib/use-debounced-value';
import { Forbidden } from '../../../../../../components/ui/state-views';
import { toasts } from '../../../../../../lib/toast-presets';

const BUCKET_LABEL: Record<ProjectBucket, string> = { active: 'Active', completed: 'Completed', archived: 'Archived' };

export const Route = createFileRoute('/subdomain/admin/_staff/businesses/$businessId/projects')({
  // Filters live in the URL so a reload or a shared link shows the same list.
  validateSearch: (search: Record<string, unknown>) => ({
    bucket: (PROJECT_BUCKETS as readonly unknown[]).includes(search.bucket)
      ? (search.bucket as ProjectBucket)
      : 'active',
    domain: (PROJECT_DOMAINS as readonly unknown[]).includes(search.domain)
      ? (search.domain as ProjectDomain)
      : undefined,
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
  }),
  // The table is 1200 wide in the design (same canvas as the other tables).
  staticData: { wide: true },
  component: BusinessProjectsPage,
});

/** The title of a side card: Heading/H4 with a muted line under it (Figma A05.1). */
function SideTitle({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-0-5)">
      <h2 className="text-heading-h4 text-text-strong">{title}</h2>
      <p className="text-body-xsmall text-text-tertiary">{sub}</p>
    </div>
  );
}

/**
 * Figma A05.1 "Allowance this cycle". TODO(api): the plan and what has been used need billing (Sprint 4); the rows are
 * drawn as designed with dashes, never with numbers the API cannot back.
 */
function AllowanceCard() {
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-3-5) px-(--mbm-space-5-5) py-(--mbm-space-5)"
    >
      <SideTitle title="Allowance this cycle" sub="Appears once billing is live" />
      {['Posts', 'Reels', 'Website updates', 'Revisions'].map((row) => (
        <div key={row} className="flex flex-col gap-(--mbm-space-1-5)">
          <div className="flex items-baseline justify-between">
            <span className="text-label-medium text-text-primary">{row}</span>
            <span className="text-body-xsmall text-text-tertiary">—</span>
          </div>
          <div className="h-(--mbm-space-1-5) w-full rounded-full bg-surface-field" />
        </div>
      ))}
    </Card>
  );
}

/**
 * Figma A05.1 "General chats": the three chats that belong to a domain, not a project.
 * TODO(api): chat arrives in Sprint 5, so each row says there is nothing in it yet and is inert.
 */
function GeneralChatsCard() {
  const toast = useToast();
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-1) px-(--mbm-space-5-5) py-(--mbm-space-5)"
    >
      <SideTitle title="General chats" sub="Not tied to a project. Task chats live inside each task." />
      <ul className="flex flex-col pt-(--mbm-space-2)">
        {[
          ['Website & tech', 'Website'],
          ['Marketing', 'Marketing'],
          ['Automation', 'Automation'],
        ].map(([name]) => (
          <li key={name}>
            <button
              type="button"
              onClick={() => toast(toasts.comingSoon('Chat arrives in Sprint 5.'))}
              className="flex w-full items-center gap-(--mbm-space-3) py-(--mbm-space-3) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <Icon icon={ChatCircle} size="md" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-label-medium text-text-primary">{name}</span>
                <span className="text-body-xsmall text-text-tertiary">No messages yet</span>
              </span>
              <Icon icon={CaretRight} size="xs" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * A business's projects for staff (Figma A05.1). There is no global project list: staff always start from a business.
 * TODO(api): the right-hand "Allowance this cycle" (billing, Sprint 4) and "General chats" (chat, Sprint 5) cards have
 * no data yet; their place is kept and they say so.
 */
function BusinessProjectsPage() {
  const { businessId } = Route.useParams();
  const { bucket, domain, q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const toast = useToast();
  const { data: session } = useSession();
  const role = session?.user.role;
  const [text, setText] = useState(q ?? '');
  const [creating, setCreating] = useState(false);
  const search = useDebouncedValue(text.trim(), 250) || undefined;

  if (search !== q) void navigate({ search: (prev) => ({ ...prev, q: search }), replace: true });

  const list = useInfiniteQuery(projectListQuery({ businessId, bucket, domain, q: search }));
  // The business header needs the directory (admins and the business's account manager); a lead or specialist who
  // reaches this page by their own work gets a plain title instead.
  const business = useQuery({ ...businessQuery(businessId), retry: false });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = list.data?.pages[0]?.counts;
  const businessName =
    business.data?.name ?? list.data?.pages[0]?.business?.name ?? items[0]?.businessName ?? 'This business';
  const forbidden = (list.error as Partial<MappedApiError> | null)?.kind === 'forbidden';
  const soon = () => toast(toasts.comingSoon('This arrives with its own sprint.'));

  // Only admins and the business's account manager create projects; reaching this list as an account manager
  // means the API already confirmed they manage it. Hide the button from everyone else.
  const canCreate =
    isStaffRoleName(role) && can({ role }, 'project.create', { managesBusiness: role === 'account_manager' });

  if (forbidden) {
    return (
      <>
        <PageHeader title="Projects" />
        <div className="rounded-(--mbm-component-radius-card) border border-border-line bg-surface-card">
          <Forbidden title="You don’t have access to this business" description="Ask an admin to assign it to you." />
        </div>
      </>
    );
  }

  const newProject = canCreate ? (
    <button type="button" onClick={() => setCreating(true)} className={pillClass('dark', 'lg')}>
      <Icon icon={Plus} size="sm" />
      New project
    </button>
  ) : null;

  return (
    <>
      {business.data ? (
        <>
          <BusinessBreadcrumb businessName={businessName} />
          <BusinessHeader business={business.data} primary={newProject} />
          <BusinessTabs
            businessId={businessId}
            active="projects"
            projectCount={business.data.counts.projects}
            deliverableCount={business.data.counts.deliverables}
          />
        </>
      ) : (
        <PageHeader title={businessName} description="Projects" actions={newProject} />
      )}

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex flex-col gap-(--mbm-space-4)">
          <div className="flex flex-wrap items-center gap-(--mbm-space-2-5)">
            <ToolbarSearch
              filled
              size="lg"
              aria-label="Search projects"
              placeholder="Search projects"
              value={text}
              onChange={setText}
              className="w-full sm:w-(--mbm-layout-toolbar-search-narrow-width)"
            />
            <SegmentedControl
              shape="pill"
              size="lg"
              options={PROJECT_BUCKETS.map((value) => ({ value, label: BUCKET_LABEL[value], count: counts?.[value] }))}
              value={bucket}
              onChange={(next) => void navigate({ search: (prev) => ({ ...prev, bucket: next }) })}
              aria-label="Project status"
            />
            <span className="ml-auto flex flex-wrap items-center gap-(--mbm-space-2-5)">
              <FilterSelectPill
                label="Domain"
                showAll
                size="lg"
                value={domain ?? ''}
                onChange={(value) =>
                  void navigate({
                    search: (prev) => ({ ...prev, domain: (value || undefined) as ProjectDomain | undefined }),
                  })
                }
                options={PROJECT_DOMAINS.map((d) => ({ value: d, label: d }))}
              />
              {/* The list is already newest activity first, the design's only order. */}
              <button type="button" onClick={soon} className={pillClass('light', 'lg')}>
                Recent activity
                <Icon icon={CaretDown} size="xs" />
              </button>
            </span>
          </div>

          <div className="overflow-hidden rounded-(--mbm-component-radius-card) bg-surface-card shadow-[var(--mbm-shadow-table-card),var(--mbm-shadow-sm)]">
            <ProjectRowsHeader />
            <InfiniteList
              items={items}
              getKey={(p) => p.id}
              renderItem={(p) => <ProjectRow project={p} />}
              hasNextPage={list.hasNextPage}
              isFetchingNextPage={list.isFetchingNextPage}
              fetchNextPage={() => void list.fetchNextPage()}
              isLoading={list.isLoading}
              isError={list.isError}
              onRetry={() => void list.refetch()}
              emptyState={
                <EmptyState
                  title={
                    search || domain
                      ? 'No projects match these filters'
                      : `No ${BUCKET_LABEL[bucket].toLowerCase()} projects`
                  }
                  description={
                    bucket === 'active' && !search && !domain
                      ? 'Create one for a custom piece of work, or wait for a plan or add-on purchase to create it automatically.'
                      : undefined
                  }
                  action={
                    canCreate && bucket === 'active' && !search && !domain ? (
                      <button type="button" onClick={() => setCreating(true)} className={pillClass('dark')}>
                        <Icon icon={Plus} size="sm" />
                        New project
                      </button>
                    ) : undefined
                  }
                />
              }
              skeletonRow={<ProjectRowSkeleton />}
              skeletonCount={2}
            />
            {list.hasNextPage || list.isFetchingNextPage ? (
              <p className="flex items-center justify-center gap-(--mbm-space-2) py-(--mbm-space-3-5) text-body-small text-text-tertiary">
                <Icon icon={CircleNotch} size="sm" />
                Loading more
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-(--mbm-space-4)">
          <AllowanceCard />
          <GeneralChatsCard />
        </div>
      </div>

      {canCreate ? (
        <NewProjectDrawer
          open={creating}
          onClose={() => setCreating(false)}
          businessId={businessId}
          businessName={businessName}
          ownerFirstName={business.data?.ownerName.split(' ')[0]}
        />
      ) : null}
    </>
  );
}
